import { useEffect, useRef } from 'react'
import type { PublicListing } from '../data/endpoints'
import { CustomerShell, rupees } from './CustomerShell'
import type { CatalogueFeed } from './useCatalogue'
import { MIN_QUERY_LENGTH } from './useCatalogue'

/**
 * Browse unsold stock. Photo first, because a second-hand book is bought on its
 * condition and a parent scrolling on a phone reads pictures before words.
 *
 * A sold-out card stays on the grid, dimmed and labelled, rather than vanishing:
 * the listing was probably shared in a WhatsApp group minutes ago and a card that
 * disappears reads as a broken link. The label carries the word "Sold", never
 * colour alone.
 *
 * The feed is passed in whole rather than assembled here (see `useCatalogue.ts`).
 * Pages accumulate, so scrolling never destroys what is already on screen - the
 * previous "Show more" refetched and replaced, which meant a customer who scrolled
 * past a book they liked could not get back to it except by starting again.
 *
 * `photoUrl` is injected rather than built from the R2 key here, so the signing
 * strategy can change without touching a screen.
 */
export interface BrowseProps {
  feed: CatalogueFeed
  creditPaise: number | null
  cart: { lines: number; payablePaise: number } | null
  photoUrl: (r2Key: string) => string
  onOpen?: (listingId: string) => void
  online?: boolean
}

export function Browse({ feed, creditPaise, cart, photoUrl, onOpen, online }: BrowseProps) {
  const searching = feed.q.trim().length >= MIN_QUERY_LENGTH
  return (
    <CustomerShell title="The Kinder Market" creditPaise={creditPaise} cart={cart} online={online}>
      <SearchField feed={feed} />

      {/* The count, always. "18 books" is what turns a scroll into a finite list,
          and it is the answer to "anything about dinosaurs?" before any scrolling. */}
      {searching && feed.status === 'ready' ? (
        <p className="sf-result-count" role="status">
          {feed.matched ?? feed.items.length} {(feed.matched ?? feed.items.length) === 1 ? 'book' : 'books'} for
          {' '}&ldquo;{feed.q.trim()}&rdquo;
        </p>
      ) : null}

      {feed.status === 'error' && feed.items.length === 0 ? (
        <div className="async async-error" role="alert">
          <p>Could not load the shelf. Check the connection.</p>
          <button type="button" className="btn" onClick={feed.retry}>
            Try again
          </button>
        </div>
      ) : null}

      {feed.status === 'loading' ? <p className="async async-loading">Loading…</p> : null}

      {feed.status === 'empty' ? (
        <p className="async async-empty">
          {searching
            ? `Nothing matching "${feed.q.trim()}" right now. Try one word - a title, an author, or an age.`
            : 'Nothing on the shelf right now - Mitali adds stock most weeks'}
        </p>
      ) : null}

      {feed.items.length > 0 ? (
        <ul className="sf-grid">
          {feed.items.map((l) => (
            <ListingCard key={l.id} listing={l} photoUrl={photoUrl} onOpen={onOpen} />
          ))}
        </ul>
      ) : null}

      <FeedFoot feed={feed} />
    </CustomerShell>
  )
}

/**
 * The search field.
 *
 * `type="search"`, not `type="text"`: it gets the phone's search keyboard and the
 * clear control for free, and clearing is the most common second action.
 * `enterKeyHint="search"` so the return key says what it does. No submit button
 * is required - results arrive while typing - but the form still submits, because
 * a person who taps the return key expects something to happen.
 */
function SearchField({ feed }: { feed: CatalogueFeed }) {
  return (
    <form className="sf-search" role="search" onSubmit={(e) => e.preventDefault()}>
      <label className="sf-search-label" htmlFor="sf-search-input">
        Search the shelf
      </label>
      <input
        id="sf-search-input"
        className="sf-search-input"
        type="search"
        name="q"
        value={feed.q}
        autoComplete="off"
        enterKeyHint="search"
        placeholder="Title, author, ISBN, age or category"
        onChange={(e) => feed.setQ(e.target.value)}
      />
    </form>
  )
}

/**
 * The end of the feed.
 *
 * The sentinel is what makes the scroll continuous; the button is what makes it
 * work when IntersectionObserver is unavailable, when motion is reduced, or when
 * someone is driving the page with a keyboard. Both are always present - the
 * button is the honest control and the observer is the convenience on top of it.
 */
function FeedFoot({ feed }: { feed: CatalogueFeed }) {
  const sentinel = useRef<HTMLDivElement | null>(null)
  const { nextCursor, loadMore, loadingMore } = feed

  useEffect(() => {
    const node = sentinel.current
    if (!node || !nextCursor) return
    if (typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore()
      },
      // Fire before the last row is reached, so the next page is usually already
      // there by the time the thumb gets to it.
      { rootMargin: '400px 0px' },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [nextCursor, loadMore])

  if (!nextCursor) {
    return feed.items.length > 0 ? (
      <p className="sf-feed-end muted">That is everything.</p>
    ) : null
  }

  return (
    <>
      <div ref={sentinel} aria-hidden="true" className="sf-sentinel" />
      <button type="button" className="btn thumb-primary" onClick={loadMore} disabled={loadingMore}>
        {loadingMore ? 'Loading…' : 'Show more'}
      </button>
    </>
  )
}

function ListingCard({
  listing,
  photoUrl,
  onOpen,
}: {
  listing: PublicListing
  photoUrl: (k: string) => string
  onOpen?: (id: string) => void
}) {
  return (
    <li className={`sf-card${listing.soldOut ? ' sf-card-sold' : ''}`}>
      <button type="button" className="sf-card-hit" onClick={() => onOpen?.(listing.id)}>
        {listing.photoR2Key ? (
          <img className="sf-photo" src={photoUrl(listing.photoR2Key)} alt={listing.name} loading="lazy" />
        ) : (
          <span className="sf-photo sf-photo-missing" aria-hidden="true" />
        )}
        <span className="sf-card-name">{listing.name}</span>
        <span className="sf-card-price">{rupees(listing.pricePaise)}</span>
        {listing.soldOut ? <span className="sf-sold-label">Sold</span> : null}
      </button>
    </li>
  )
}
